import React, { useState, useEffect } from "react";
import FadeAlert, {
  FadeAlertType,
} from "../../components/FadeAlert/FadeAlert.tsx";
import PolicyService from "../../services/PolicyService/PolicyService.ts";
import { useAuth } from "../../provider/AuthProvider.tsx";
import ExpiringPoliciesDataGrid from "../../components/DataTables/ExpiringPoliciesDataGrid.tsx";
import {
  normalizeDate,
  normalizeInstallmentProperties,
  normalizePolicyProperties,
} from "../../utils/NormalizationUtil.ts";
import InsuranceService from "../../services/InsuranceService/InsuranceService.ts";
import DatePicker from "../../components/Forms/DatePicker/DatePicker.tsx";
import SelectGroup from "../../components/Forms/SelectGroup/SelectGroup.tsx";
import {
  PolicyReferenceProps,
  PolicyTypeEnum,
  PolicyURLEnum,
} from "../../enum/policy-fetch-type.enum.ts";
import {
  ExpiringPoliciesObject,
  PoliciesInstallmentsObject,
} from "../../types/insurance.ts";
import { useLocation } from "react-router-dom";
import moment from "moment";
import Breadcrumb from "../../components/Breadcrumbs/Breadcrumb.tsx";
import SectionHeader from "../../components/SectionHeader/SectionHeader.tsx";
import SequentialPoliciesDataGrid from "../../components/DataTables/SequentialPoliciesDataGrid.tsx";
import { inputClass, labelClass } from "../../constants/formStyles";

const PolicyReference: React.FC<PolicyReferenceProps> = () => {
  const [formState, setFormState] = useState({
    fromDate: "",
    toDate: "",
    company: "",
    policyType: "",
    agent: "",
    office: "",
    identificationNumber: "",
  });
  const [alert, setAlert] = useState<string | null>(null);
  const [alertType, setAlertType] = useState<FadeAlertType>();
  const { jwt } = useAuth();
  const [agentOptions, setAgentOptions] = useState<{ [key: string]: string }>(
    {}
  );
  const [insuranceCompanies, setInsuranceCompanies] = useState<{
    [key: string]: string;
  }>({});
  const [offices, setOffices] = useState<{ [key: string]: string }>({});
  const [policyTypes, setPolicyTypes] = useState<{ [key: string]: string }>({});
  const [policies, setPolicies] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isDateInvalid, setIsDateInvalid] = useState(false);
  const [policyFetchType, setPolicyFetchType] = useState<string>("");
  const location = useLocation();
  const { isAdmin } = useAuth();

  useEffect(() => {
    if (location.pathname === PolicyURLEnum.Expiring) {
      setPolicyFetchType(PolicyTypeEnum.Expiring);
    } else {
      setPolicyFetchType(PolicyTypeEnum.Installments);
    }
    setFormState({
      fromDate: "",
      toDate: "",
      company: "",
      policyType: "",
      agent: "",
      office: "",
      identificationNumber: "",
    });
    setPolicies([]);
    setAlert(null);
    setAlertType(undefined);
  }, [location.pathname]);

  useEffect(() => {
    const fetchData = async (): Promise<void> => {
      try {
        setIsLoading(true);
        const [agents, companies, officesRes, policyTypesRes] =
          await Promise.all([
            InsuranceService.getAllAgents(jwt),
            InsuranceService.getAllInsuranceCompanies(jwt, false),
            InsuranceService.getAllOffices(jwt),
            InsuranceService.getAllPolicyTypes(jwt),
          ]);
        setAgentOptions(agents as { [key: string]: string });
        setInsuranceCompanies(companies as { [key: string]: string });
        setOffices(officesRes as { [key: string]: string });
        setPolicyTypes(policyTypesRes as { [key: string]: string });
      } catch (err: any) {
        throw new Error(err.message);
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [jwt]);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => {
      const newState = { ...prevState, [name]: value };
      if (newState.fromDate && newState.toDate) {
        validateDates(newState.fromDate, newState.toDate);
      } else {
        setIsDateInvalid(false);
        setAlert(null);
      }
      return newState;
    });
  };

  const validateDates = (fromDate: string, toDate: string) => {
    const from = moment(fromDate, "DD/MM/YYYY");
    const to = moment(toDate, "DD/MM/YYYY");
    const diffInMonths = to.diff(from, "months", true);
    if (diffInMonths > 3 && !isAdmin) {
      setIsDateInvalid(true);
      setAlert("Датите не трябва да бъдат с разлика повече от 3 месеца.");
      setAlertType(FadeAlertType.error);
    } else if (diffInMonths >= 12 && isAdmin) {
      setIsDateInvalid(true);
      setAlert("Датите не трябва да бъдат с разлика повече от 1 година.");
      setAlertType(FadeAlertType.error);
    } else {
      setIsDateInvalid(false);
      setAlert(null);
      setAlertType(null);
    }
  };

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    const { fromDate, toDate } = formState;
    if (!fromDate || !toDate) {
      setAlert("Моля, въведете стартова и крайна дата");
      setAlertType(FadeAlertType.error);
      return;
    }
    try {
      setIsLoading(true);
      const callfunc =
        policyFetchType === PolicyTypeEnum.Expiring
          ? "getEndPolicy"
          : "getPendValiors";
      const result = await PolicyService.fetchPolicies(
        jwt,
        formState,
        callfunc
      );
      const normalizedPolicies:
        | ExpiringPoliciesObject[]
        | PoliciesInstallmentsObject[] = Object.values(result).map((policy) => {
        const normalizedPolicy =
          policyFetchType === PolicyTypeEnum.Expiring
            ? normalizePolicyProperties(policy)
            : normalizeInstallmentProperties(policy);

        if (policyFetchType === PolicyTypeEnum.Expiring) {
          return {
            ...normalizedPolicy,
            fromDate: normalizeDate(normalizedPolicy.fromDate.trim()),
            toDate: normalizeDate(normalizedPolicy.toDate.trim()),
          };
        } else {
          return {
            ...normalizedPolicy,
            paymentDate: normalizeDate(normalizedPolicy.paymentDate.trim()),
          };
        }
      });
      setPolicies(normalizedPolicies);
    } catch (error) {
      setAlert("Грешка при зареждането на полици.");
      setAlertType(FadeAlertType.error);
    } finally {
      setIsLoading(false);
    }
    setAlert(null);
  };

  const heading =
    policyFetchType === PolicyTypeEnum.Expiring
      ? "Изтичащи полици"
      : "Поредни вноски";

  return (
    <>
      <Breadcrumb pageName={heading}></Breadcrumb>
      <FadeAlert
        alert={alert}
        type={alertType}
        onClose={() => setAlert(null)}
      />

      <fieldset className="border-stroke dark:border-strokedark shadow-default dark:bg-boxdark rounded-sm border bg-white">
        <SectionHeader title={heading} />

        <form onSubmit={handleSearch} className="p-3 pt-1">
          {/* Row 1 - dates + filters */}
          <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
            <div>
              <DatePicker
                label="От дата"
                name="fromDate"
                onChange={handleInputChange}
                value={formState.fromDate}
                classNameAdditional={isDateInvalid ? "border-rose-500" : ""}
              />
            </div>
            <div>
              <DatePicker
                label="До дата"
                name="toDate"
                onChange={handleInputChange}
                value={formState.toDate}
                classNameAdditional={isDateInvalid ? "border-rose-500" : ""}
              />
            </div>
            <div>
              <label className={labelClass}>ЗК</label>
              <SelectGroup
                value={formState.company}
                placeholder="Изберете компания"
                options={insuranceCompanies}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, company: value }))
                }
                name="company"
              />
            </div>
            <div>
              <label className={labelClass}>Вид Полица</label>
              <SelectGroup
                value={formState.policyType}
                placeholder="Изберете вид полица"
                options={policyTypes}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, policyType: value }))
                }
                name="policyType"
              />
            </div>
            <div>
              <label className={labelClass}>Агент</label>
              <SelectGroup
                value={formState.agent}
                placeholder="Изберете агент"
                options={agentOptions}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, agent: value }))
                }
                name="agent"
              />
            </div>
            <div>
              <label className={labelClass}>Офис</label>
              <SelectGroup
                value={formState.office}
                placeholder="Изберете офис"
                options={offices}
                onChange={(value) =>
                  setFormState((prev) => ({ ...prev, office: value }))
                }
                name="office"
              />
            </div>
            <div>
              <label className={labelClass}>ЕГН/Булстат</label>
              <input
                type="text"
                id="identificationNumber"
                name="identificationNumber"
                value={formState.identificationNumber}
                onChange={handleInputChange}
                className={inputClass}
              />
            </div>

            <div className="flex items-end">
              <div className="group relative w-full">
                <button
                  type="submit"
                  disabled={isDateInvalid}
                  className={`w-full rounded px-3 py-2 text-xs font-medium text-white ${
                    isDateInvalid
                      ? "bg-graydark cursor-not-allowed opacity-50"
                      : "bg-primary hover:bg-opacity-90"
                  }`}
                >
                  Покажи
                </button>
                {isDateInvalid && (
                  <div className="bg-primary absolute bottom-full left-1/2 z-20 mb-2 -translate-x-1/2 whitespace-nowrap rounded px-3 py-2 text-xs text-white opacity-0 group-hover:opacity-100">
                    <span className="bg-primary absolute bottom-[-3px] left-1/2 -z-10 h-2 w-2 -translate-x-1/2 rotate-45 rounded-sm" />
                    Датите не могат да са в диапазон по-голям от 3 месеца
                  </div>
                )}
              </div>
            </div>
          </div>
        </form>
      </fieldset>

      {policyFetchType === PolicyTypeEnum.Expiring ? (
        <ExpiringPoliciesDataGrid
          policies={policies}
          isLoading={isLoading}
          fromDate={formState.fromDate}
          toDate={formState.toDate}
        />
      ) : (
        <SequentialPoliciesDataGrid
          policies={policies}
          isLoading={isLoading}
          fromDate={formState.fromDate}
          toDate={formState.toDate}
        />
      )}
    </>
  );
};

export default PolicyReference;
